import { useEffect, useState } from "react";
import { ListChecks, CheckCircle, Hourglass, WarningOctagon, Megaphone } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { AnnouncementDetail, AnnouncementsAllSheet } from "../components/AnnouncementModals";

export default function ManagerHome() {
  const { user, isFeatureEnabled } = useAuth();
  const [data, setData] = useState(null);
  const [ann, setAnn] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const load = () => {
      api.get("/api/manager/dashboard").then(({ data }) => setData(data)).catch(() => {});
      api.get("/api/announcements").then(({ data }) => setAnn(data.items || [])).catch(() => {});
    };
    load();
    const id = setInterval(load, 30000); // auto-refresh so new announcements/tickets appear
    return () => clearInterval(id);
  }, []);

  return (
    <div className="space-y-5 font-sans" data-testid="manager-home">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">Today · {user?.role?.toLowerCase()}</p>
          <h1 className="font-display text-3xl font-extrabold text-white">{user?.name?.split(" ")[0] || "Hi"} <span className="text-neon-cyan">·</span></h1>
          <p className="mt-1 truncate text-sm text-white/60">Silver Altire</p>
        </div>
      </div>

      {isFeatureEnabled("complaints") && (
        <div className="grid grid-cols-3 gap-2.5">
          <Stat icon={WarningOctagon} accent="pink" label="Open" value={data?.counts?.open ?? "—"} onClick={() => navigate("/complaints")} />
          <Stat icon={Hourglass} accent="amber" label="Working" value={data?.counts?.inProgress ?? "—"} onClick={() => navigate("/complaints")} />
          <Stat icon={CheckCircle} accent="lime" label="Resolved" value={data?.counts?.resolvedToday ?? "—"} onClick={() => navigate("/complaints")} />
        </div>
      )}

      {isFeatureEnabled("checklist") && (
        <button onClick={() => navigate("/checklist")} className="btn-primary w-full" data-testid="manager-daily-checklist">
          <ListChecks size={16} weight="fill" className="mr-1.5" /> Daily Checklist
        </button>
      )}

      <div>
        <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.22em] text-white/55">Quick access</p>
        <div className="grid grid-cols-2 gap-2.5">
          {[
            { to: "/tasks", label: "Tasks", moduleKey: "complaints" },
            { to: "/daily-report", label: "Daily Report" },
            { to: "/flats", label: "Flat Directory", moduleKey: "flats" },
            { to: "/tenants", label: "Tenants", moduleKey: "tenants" },
            { to: "/staff/SECURITY", label: "Security", moduleKey: "attendance" },
            { to: "/staff/HOUSEKEEPING", label: "Housekeeping", moduleKey: "attendance" },
            ...(["CHAIRPERSON", "ADMIN"].includes(user?.role) ? [{ to: "/oversight", label: "Oversight", moduleKey: "oversight" }] : []),
            ...(user?.role === "ADMIN" ? [{ to: "/admin", label: "Users & Roles" }] : []),
          ].filter((l) => isFeatureEnabled(l.moduleKey)).map((l) => (
            <button key={l.to} onClick={() => navigate(l.to)} data-testid={`mgr-quick-${l.to.replace("/", "")}`}
              className="card flex items-center justify-between p-3.5 text-left transition-all hover:border-white/20 active:scale-[0.98]">
              <span className="font-display text-[14px] font-semibold text-white">{l.label}</span>
              <span className="text-neon-cyan">→</span>
            </button>
          ))}
        </div>
      </div>

      {isFeatureEnabled("announcements") && ann.length > 0 && (
        <div>
          <div className="mb-3 flex items-end justify-between">
            <h2 className="font-display text-lg font-bold text-white">Announcements</h2>
            {ann.length > 3 && <button onClick={() => setShowAll(true)} className="text-[11px] font-bold uppercase tracking-wider text-neon-cyan">See all</button>}
          </div>
          <div className="grid gap-2.5 md:grid-cols-2">
            {ann.slice(0, 4).map((a) => (
              <button key={a.id} onClick={() => setSelected(a)} className="card p-4 text-left hover:border-white/20">
                <div className="mb-1 flex items-center gap-2">
                  <Megaphone size={14} weight="fill" className="text-neon-lime" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-neon-lime">{a.tag}</span>
                  {Array.isArray(a.images) && a.images.length > 0 && <span className="ml-auto text-[10px] text-neon-cyan">{a.images.length} 🖼</span>}
                </div>
                <p className="font-display text-[15px] font-semibold leading-snug text-white">{a.title}</p>
                <p className="mt-1 line-clamp-1 text-[13px] text-white/60">{a.body}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {isFeatureEnabled("complaints") && (
      <div>
        <div className="mb-3 flex items-end justify-between">
          <h2 className="font-display text-lg font-bold text-white">Latest activity</h2>
          <span className="text-[11px] uppercase tracking-[0.22em] text-white/40">live</span>
        </div>
        {!data?.recent?.length ? (
          <div className="card p-6 text-center text-sm text-white/55">No tickets yet. The board is clean.</div>
        ) : (
          <div className="space-y-2">
            {data.recent.map((c, i) => (
              <motion.button key={c.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                onClick={() => navigate(`/complaints/${c.id}`)}
                className="card flex w-full items-center gap-3 p-3.5 text-left transition-all hover:border-white/20 active:scale-[0.99]">
                <span className={`h-2 w-2 shrink-0 rounded-full ${
                  c.status === "OPEN" ? "bg-neon-pink animate-pulse-soft" :
                  c.status === "IN_PROGRESS" ? "bg-neon-amber" : "bg-neon-lime"
                }`} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold text-white">{c.title}</p>
                  <p className="text-[11px] uppercase tracking-wider text-white/50">Flat {c.flatId || "—"} · {c.category}</p>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-white/40">{c.status.replace("_", " ")}</span>
              </motion.button>
            ))}
          </div>
        )}
      </div>
      )}

      <AnimatePresence>
        {selected && <AnnouncementDetail a={selected} onClose={() => setSelected(null)} />}
        {showAll && <AnnouncementsAllSheet items={ann} onClose={() => setShowAll(false)} onOpen={(a) => { setShowAll(false); setSelected(a); }} />}
      </AnimatePresence>
    </div>
  );
}

function Stat({ icon: Icon, accent, label, value, onClick }) {
  const ring = accent === "pink" ? "text-neon-pink" : accent === "amber" ? "text-neon-amber" : "text-neon-lime";
  const glow = accent === "pink" ? "shadow-glow-pink" : accent === "amber" ? "" : "shadow-glow-lime";
  return (
    <button onClick={onClick} className={`card p-4 text-left transition-all hover:border-white/20 active:scale-[0.97] ${glow}`}>
      <Icon size={18} weight="duotone" className={ring} />
      <p className="mt-2 font-display text-2xl font-extrabold text-white">{value}</p>
      <p className="text-[10px] font-bold uppercase tracking-wider text-white/55">{label}</p>
    </button>
  );
}
